import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

/**
 * Related Stories rail - 3 (or up to 6) other stories shown at the bottom
 * of every article. Ranked by tag overlap, falling back to most recent.
 *
 * Style: editorial grid, matte-grey cards, hover lifts a red rule below
 * the headline. No shadows, no gradients - matches the VICE aesthetic.
 */

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
    }).toUpperCase();
  } catch {
    return '';
  }
}

export default function RelatedStories({ slug, limit = 3 }) {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!slug || !API) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API}/api/articles/${slug}/related?limit=${limit}`,
        );
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        if (!cancelled) {
          setItems(data.items || []);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, limit]);

  if (!loaded || items.length === 0) return null;

  return (
    <section
      className="mt-16 pt-10 border-t border-zinc-800"
      data-testid="related-stories"
    >
      <div className="flex items-baseline justify-between mb-6">
        <h2 className="text-white font-black text-xl tracking-tight uppercase">
          Keep Reading
        </h2>
        <Link
          to="/stories"
          className="text-red-400 hover:text-red-300 text-xs font-bold uppercase tracking-widest flex items-center gap-1"
          data-testid="related-stories-all"
        >
          All Stories <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((a) => (
          <Link
            key={a.id || a.slug}
            to={`/stories/${a.slug}`}
            className="group block bg-zinc-900/40 border border-zinc-800 hover:border-red-900/60 transition-colors"
            data-testid={`related-story-${a.slug}`}
          >
            {a.cover_image_url ? (
              <div className="aspect-[16/10] overflow-hidden bg-zinc-950">
                <img
                  src={a.cover_image_url}
                  alt={a.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                />
              </div>
            ) : (
              <div className="aspect-[16/10] bg-zinc-950 flex items-center justify-center">
                <span className="text-zinc-700 text-xs uppercase tracking-widest">Outlaw Media</span>
              </div>
            )}
            <div className="p-4">
              <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-2">
                {formatDate(a.published_at)}
              </p>
              <h3 className="text-white font-bold text-base leading-snug line-clamp-3 group-hover:text-red-300 transition-colors">
                {a.title}
              </h3>
              {a.excerpt && (
                <p className="text-zinc-400 text-xs mt-2 line-clamp-2">
                  {a.excerpt}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
